import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Button,
  useColorModeValue,
  Icon,
  SimpleGrid,
  Card,
  CardBody,
  Avatar,
  Badge,
  Container,
  useBreakpointValue,
  Center,
  Image
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { colors } from '../../assets/styles/colors';
import {
  FiAward,
  FiTarget,
  FiTrendingUp,
  FiStar,
  FiHeart,
  FiZap,
  FiCpu,
  FiPlay
} from 'react-icons/fi';
import HansCardLogo from '@/assets/images/HansCard.svg';

interface WelcomeProps {
  onNext: (data?: Record<string, unknown>) => void;
  onPrevious: () => void;
  stepData: Record<string, unknown>;
  isLoading: boolean;
}

const Welcome: React.FC<WelcomeProps> = ({ onNext, isLoading }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // Gaming-inspired theme colors
  const bgGradient = useColorModeValue(
    'linear(to-br, gray.50, blue.50, purple.50)',
    'linear(to-br, gray.900, blue.900, purple.900)'
  );
  const cardBg = useColorModeValue('white', colors.primary[700]);

  const textColor = useColorModeValue('gray.800', 'white');
  const mutedTextColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('blue.500', 'blue.400');
  const successColor = useColorModeValue('green.500', 'green.400');

  // Responsive values
  const cardColumns = useBreakpointValue({ base: 1, md: 2 });
  const testimonialColumns = useBreakpointValue({ base: 1, lg: 3 });

  const features = [
    {
      icon: FiCpu,
      title: t('onboarding.welcome.features.smartLearning.title'),
      description: t('onboarding.welcome.features.smartLearning.description'),
      color: 'purple',
      gradient: 'linear(to-r, purple.400, pink.400)'
    },
    {
      icon: FiTarget,
      title: t('onboarding.welcome.features.personalizedPath.title'),
      description: t('onboarding.welcome.features.personalizedPath.description'),
      color: 'blue',
      gradient: 'linear(to-r, blue.400, cyan.400)'
    },
    {
      icon: FiTrendingUp,
      title: t('onboarding.welcome.features.progressTracking.title'),
      description: t('onboarding.welcome.features.progressTracking.description'),
      color: 'green',
      gradient: 'linear(to-r, green.400, teal.400)'
    },
    {
      icon: FiZap,
      title: t('onboarding.welcome.features.quickSessions.title'),
      description: t('onboarding.welcome.features.quickSessions.description'),
      color: 'orange',
      gradient: 'linear(to-r, orange.400, yellow.400)'
    }
  ];

  const testimonials = [
    {
      name: 'Gilad',
      avatar: '😍',
      text: t('onboarding.welcome.testimonials.gilad'),
      language: 'Dutch',
      level: 'Advanced',
      color: 'blue'
    },
    {
      name: 'Hans',
      avatar: '👨‍🎓',
      text: t('onboarding.welcome.testimonials.hans'),
      language: 'Hebrew',
      level: 'Advanced',
      color: 'green'
    },
    {
      name: 'Fruzsina',
      avatar: '👩‍🔬',
      text: t('onboarding.welcome.testimonials.fruzsina'),
      language: 'Hebrew',
      level: 'Intermediate',
      color: 'purple'
    }
  ];

  const methodologySteps = [
    {
      icon: FiTarget,
      title: t('onboarding.welcome.methodology.assess'),
      color: 'blue'
    },
    {
      icon: FiCpu,
      title: t('onboarding.welcome.methodology.adapt'),
      color: 'purple'
    },
    {
      icon: FiTrendingUp,
      title: t('onboarding.welcome.methodology.achieve'),
      color: 'green'
    }
  ];

  return (
    <Box minH="100vh" bgGradient={bgGradient}>
      <Container maxW="6xl" py={8}>
        <VStack spacing={12} align="stretch">
          {/* Gaming-style Header */}
          <VStack spacing={6} textAlign="center">

            <VStack spacing={4}>
              <Image
                src={HansCardLogo}
                alt="HansCard Logo"
                h="80px"
                w="auto"
                filter="drop-shadow(0 0 8px rgba(0, 0, 0, 0.2))"
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent'
                }}
              />

              <Text fontSize="xl" color={mutedTextColor} maxW="3xl" textAlign="center">
                {t('onboarding.welcome.subtitle')}
              </Text>

              {user && (
                <Card bg={cardBg} shadow="lg" borderRadius="2xl" mt={4}>
                  <CardBody p={4}>
                    <HStack spacing={3}>
                      <Avatar
                        size="md"
                        name={user.email || ''}
                        bg={accentColor}
                        color="white"
                      />
                      <VStack spacing={1} align="start">
                        <Text fontWeight="bold" color={textColor}>
                          {t('onboarding.welcome.greeting', { name: user.email?.split('@')[0] || 'there' })}
                        </Text>
                      </VStack>
                    </HStack>
                  </CardBody>
                </Card>
              )}
            </VStack>
          </VStack>

          {/* Features Grid */}
          <VStack spacing={8}>
            <VStack spacing={3} textAlign="center">
              <Heading size="lg" color={textColor}>
                {t('onboarding.welcome.whyHansCard')}
              </Heading>
              <Text color={mutedTextColor} maxW="2xl">
                Experience the future of language learning with our AI-powered platform
              </Text>
            </VStack>

            <SimpleGrid columns={cardColumns} spacing={6} w="full">
              {features.map((feature, index) => (
                <Card
                  key={index}
                  bg={cardBg}
                  shadow="xl"
                  borderRadius="2xl"
                  border="2px"
                  borderColor="transparent"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: 'translateY(-4px)',
                    shadow: '2xl',
                    borderColor: `${feature.color}.400`
                  }}
                >
                  <CardBody p={6}>
                    <VStack spacing={4} align="start">
                      <Box
                        w="16"
                        h="16"
                        borderRadius="2xl"
                        bgGradient={feature.gradient}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        transition="all 0.3s ease"
                        _groupHover={{ transform: 'scale(1.1)' }}
                      >
                        <Icon as={feature.icon} fontSize="2xl" color="white" />
                      </Box>

                      <VStack spacing={2} align="start">
                        <Heading size="md" color={textColor}>
                          {feature.title}
                        </Heading>
                        <Text color={mutedTextColor} lineHeight="tall">
                          {feature.description}
                        </Text>
                      </VStack>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </VStack>

          {/* Learning Methodology */}
          <Card bg={cardBg} shadow="2xl" borderRadius="3xl" border="2px" borderColor={accentColor}>
            <CardBody p={8}>
              <VStack spacing={8}>
                <VStack spacing={3} textAlign="center">
                  <Heading size="lg" color={textColor}>
                    {t('onboarding.welcome.methodology.title')}
                  </Heading>
                  <Text color={mutedTextColor}>
                    Our proven 3-step approach to language mastery
                  </Text>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
                  {methodologySteps.map((step, index) => (
                    <VStack key={index} spacing={3} align="center">
                      <Box
                        w="16"
                        h="16"
                        borderRadius="full"
                        bg={`${step.color}.100`}
                        color={`${step.color}.600`}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        position="relative"
                      >
                        <Icon as={step.icon} fontSize="2xl" />
                        <Box
                          position="absolute"
                          top="-2"
                          right="-2"
                          w="6"
                          h="6"
                          bg={`${step.color}.500`}
                          borderRadius="full"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          color="white"
                          fontSize="xs"
                          fontWeight="bold"
                        >
                          {index + 1}
                        </Box>
                      </Box>
                      <Text fontWeight="bold" color={textColor} textAlign="center">
                        {step.title}
                      </Text>
                    </VStack>
                  ))}
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>

          {/* Testimonials */}
          <VStack spacing={8}>
            <VStack spacing={3} textAlign="center">
              <Heading size="lg" color={textColor}>
                {t('onboarding.welcome.testimonials.title')}
              </Heading>
              <Text color={mutedTextColor}>
                Join thousands of successful language learners
              </Text>
            </VStack>

            <SimpleGrid columns={testimonialColumns} spacing={6} w="full">
              {testimonials.map((testimonial, index) => (
                <Card
                  key={index}
                  bg={cardBg}
                  shadow="lg"
                  borderRadius="2xl"
                  transition="all 0.3s ease"
                  _hover={{ transform: 'translateY(-2px)', shadow: 'xl' }}
                >
                  <CardBody p={6}>
                    <VStack spacing={4}>
                      <HStack spacing={3} w="full">
                        <Text fontSize="2xl">{testimonial.avatar}</Text>
                        <VStack spacing={1} align="start" flex="1">
                          <Text fontWeight="bold" color={textColor}>
                            {testimonial.name}
                          </Text>
                          <HStack spacing={2}>
                            <Badge colorScheme={testimonial.color} variant="subtle">
                              {testimonial.language}
                            </Badge>
                            <Badge colorScheme="gray" variant="outline">
                              {testimonial.level}
                            </Badge>
                          </HStack>
                        </VStack>
                      </HStack>

                      <Text color={mutedTextColor} fontSize="sm" fontStyle="italic">
                        "{testimonial.text}"
                      </Text>

                      <HStack spacing={1}>
                        {[...Array(5)].map((_, i) => (
                          <Icon key={i} as={FiStar} color="yellow.400" fontSize="sm" />
                        ))}
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </VStack>

          {/* Continue Button */}
          <VStack spacing={4}>
            <Button
              size="xl"
              px={12}
              py={6}
              fontSize="lg"
              fontWeight="bold"
              borderRadius="2xl"
              bgGradient="linear(to-r, blue.500, purple.500)"
              color="white"
              rightIcon={<Icon as={FiZap} />}
              onClick={(e) => {
                e.preventDefault();
                onNext();
              }}
              isLoading={isLoading}
              loadingText={t('onboarding.welcome.starting')}
              _hover={{
                bgGradient: "linear(to-r, blue.600, purple.600)",
                transform: "translateY(-2px)",
                shadow: "xl"
              }}
              _active={{
                transform: "translateY(0)",
              }}
              transition="all 0.3s ease"
            >
              {t('onboarding.welcome.getStarted')}
            </Button>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default Welcome;